import type { EntityFieldMeta, ViewFieldMeta } from './viewTypes'

// AB#2217 — the ONE place an entity definition becomes the renderer's field-metadata map.
//
// This existed as three near-identical `toMeta` copies (tenant ViewPage, tenant EntityDetailPage, and
// an abbreviated one inside the designer's preview). The designer's copy had silently drifted: it read
// `label`/`enum`, which the DESIGNER-shape JSON spells `displayName`/`values`, so parent labels and
// enum options came out blank in the canvas — and it never registered child collections at all, so a
// line-items-table had no metadata for ANY child. Runtime was correct, the preview was not, which is
// exactly the drift AB#1866 collapsed the two renderers to prevent.
//
// The two shapes this must absorb, both legitimate and both in the tree:
//   authoring / designer  →  displayName, values
//   runtime / tenant      →  label,       enum | options
// A field can also carry `children` (an embedded collection declared on the parent entity rather than
// as a top-level entity — customers CustomerAccount.companySegments is the live example).

export interface EntityFieldMetaFieldSource {
    quantityUnitField?: string | undefined
    name: string
    // authoring spells it displayName; runtime spells it label.
    label?: string | undefined
    displayName?: string | undefined
    type: string
    required?: boolean | undefined
    // AB#3019 — what a missing REQUIRED value means in the FORM: "error" (the default — Save is
    // blocked / the cell is flagged red) or "warning" (flagged amber, and the user may proceed). A
    // requisition's estimated price is the motivating case: the requester is often exactly the person
    // who does not know it. Only meaningful alongside `required`.
    // Spelled the same in both the authoring and runtime shapes, so unlike label/enum it needs no alias.
    requiredSeverity?: 'error' | 'warning' | undefined
    readOnly?: boolean | undefined
    instant?: boolean | undefined
    // AB#2959 — writable while the record is new, locked once it exists. Spelled the same in both
    // shapes.
    immutableAfterCreate?: boolean | undefined
    // AB#2881 — this field's change re-opens an approved document. Spelled the same in both shapes.
    releaseRelevant?: boolean | undefined
    // AB#3589 — per-value write permission on an enum field. Spelled the same in both shapes.
    valueWritePermissions?: Record<string, string> | undefined
    // authoring spells enum members `values`; runtime sends `enum` or `options`.
    // AB#2913 — an entry is a bare code or a { value, label } pair; normalise with enumOptions().
    // AB#3281 — an option may also carry a semantic status tone; toMeta passes the entries through
    // untouched, so the tone rides ViewFieldMeta.enum to the pills with no extra mapping.
    enum?: (string | { value: string; label?: string | null; tone?: string | null })[] | undefined
    values?: (string | { value: string; label?: string | null; tone?: string | null })[] | undefined
    options?: (string | { value: string; label?: string | null; tone?: string | null })[] | undefined
    compute?: string | undefined
    copyFrom?: { ref: string; field: string } | { ref: string; field: string }[] | undefined
    defaultFrom?: { preferCopyFrom?: boolean } | undefined
    customType?: string | undefined
    group?: string | undefined
    lookupField?: ViewFieldMeta['lookupField'] | undefined
    // AB#2647 — entity-declared picker cascade, spelled the same in both shapes.
    filterBy?: ViewFieldMeta['filterBy'] | undefined
    lookupType?: string | undefined
    facets?: ViewFieldMeta['facets'] | undefined
    // AB#2382 — user-facing field hint. Spelled the same in both shapes (authoring and runtime),
    // so unlike label/enum it needs no aliasing.
    helpText?: string | undefined
    // AB#2447 — field-level read security: the value arrives MASKED and the block offers an
    // audited reveal. Spelled the same in both shapes.
    sensitive?: boolean | undefined
    // AB#2767 — the per-field history affordance flag. Spelled the same in both shapes.
    fieldHistory?: boolean | undefined
    // AB#2652/AB#2654 — options sourced from another entity's metadata. Spelled the same in both
    // shapes.
    metaOptions?: ViewFieldMeta['metaOptions'] | undefined
}

export interface EntityFieldMetaSource {
    /** The key the renderer will look this up by — an entity name, or a child collection's `name`. */
    key: string
    fields?: readonly EntityFieldMetaFieldSource[] | undefined
    /** Embedded child collections; each is registered under its own `name`. */
    children?: readonly { name: string; fields?: readonly EntityFieldMetaFieldSource[] | undefined }[] | undefined
    /**
     * Optional per-field lookupField overrides, derived from the entity's relationships. Applies to
     * this source's own fields only — an embedded child has no relationships array, so its explicit
     * lookupField is always passed through untouched.
     */
    lookupFields?: Record<string, ViewFieldMeta['lookupField']> | undefined
}

function toMeta(
    fields: readonly EntityFieldMetaFieldSource[],
    lookupFields?: Record<string, ViewFieldMeta['lookupField']> | undefined,
): ViewFieldMeta[] {
    return fields.map(f => ({
        name: f.name,
        // No `?? f.name` fallback: the renderer already falls back, and matching runtime exactly is
        // the point of sharing this.
        label: f.label ?? f.displayName,
        type: f.type,
        required: f.required,
        // AB#3019 — carry the severity of a missing required value, so the line grid can flag a
        // requisition's absent estimate without blocking the save the way a missing price does.
        requiredSeverity: f.requiredSeverity,
        readOnly: f.readOnly,
        instant: f.instant,
        // AB#2959 — carry the immutable-after-create flag so the slots can lock the field once the
        // bound record exists. Dropping it leaves the field editable on every saved record.
        immutableAfterCreate: f.immutableAfterCreate,
        // AB#2881 — carry the release-relevant flag through the mapper (the drop point where six
        // previous flags have silently vanished from the renderer).
        releaseRelevant: f.releaseRelevant,
        // AB#3589 — carry the per-value write permissions through the same drop point.
        valueWritePermissions: f.valueWritePermissions,
        enum: f.enum ?? f.values ?? f.options,
        compute: f.compute,
        // AB#2518 — carry copyFrom so the form can seed from a referenced record.
        // Composed defaults are resolved atomically by the host's create-default
        // binding; an independent master lookup would race the company fallback.
        copyFrom: f.defaultFrom?.preferCopyFrom ? undefined : f.copyFrom,
        customType: f.customType,
        group: f.group,
        // AB#1786 — carry lookupField so an FK renders as a searchable select of the referenced
        // entity. Dropping it degrades the field to a raw-UUID text box: 12 of the 16
        // companySegments fields are entity-ref with an explicit lookupField.
        lookupField: lookupFields?.[f.name] ?? f.lookupField,
        // AB#2647 — carry filterBy so a child-grid cell's picker can cascade off a sibling column
        // (the Type column offering only the picked Document's ACTIVE types). Dropping it silently
        // un-cascades every declared picker — the lookupField failure mode, one key over.
        filterBy: f.filterBy,
        // AB#2266 — carry lookupType so a lookupValue field renders the reference-data value select
        // (code stored, label shown). Dropping it degrades the field to a free-text input.
        lookupType: f.lookupType,
        // AB#2387 — carry facets so the form can enforce them. Dropping them meant every declared
        // constraint (nonNegative, min/max, pattern, maxLength) was silently unenforced client-side.
        facets: f.facets,
        quantityUnitField: f.quantityUnitField,
        // AB#2382 — carry the user-facing hint so the renderer can offer the info tooltip beside the
        // label. This literal is exhaustive by construction: a property omitted here is dropped for
        // every view in the product, which is the failure mode entityFieldMeta.test.ts guards.
        helpText: f.helpText,
        // AB#2447 — carry the sensitive flag so the block can offer the audited reveal affordance.
        sensitive: f.sensitive,
        // AB#2767 — carry the history flag so the renderer can offer the field's change history.
        fieldHistory: f.fieldHistory,
        // AB#2652/AB#2654 — carry metaOptions so the field renders the metadata-sourced select
        // (statuses / field names of the entity the driving sibling names). Dropping it degrades the
        // field back to the free-text input the primitive exists to replace.
        metaOptions: f.metaOptions,
    }))
}

/**
 * Build the renderer's `entityFields` map from one or more entity definitions.
 *
 * Ordering is deliberate and must not change: for each source the entity itself is registered first,
 * then its children. That mirrors the tenant loop exactly, so on a name collision the preview and the
 * runtime pick the same winner and cannot disagree.
 */
export function buildEntityFieldMeta(sources: readonly EntityFieldMetaSource[]): EntityFieldMeta {
    const map: EntityFieldMeta = {}
    for (const src of sources) {
        if (!src.key) { continue }
        map[src.key] = toMeta(src.fields ?? [], src.lookupFields)
        for (const child of src.children ?? []) {
            if (!child.name) { continue }
            // Keyed by the child's `name` verbatim — the renderer indexes
            // entityFields[component.childEntityType] bare, never by label or a qualified path.
            map[child.name] = toMeta(child.fields ?? [])
        }
    }
    return map
}
